// Dev fixtures. The authoritative API contract lives in ./types.ts; the
// inline `export type` declarations below mirror those shapes so existing
// `import { X } from "@/data/mock"` sites keep compiling.

export const family = {
  parents: [
    { id: "p1", name: "Sarah", initials: "S", color: "#4F7A5B" },
    { id: "p2", name: "Mike", initials: "M", color: "#3D5A44" },
  ],
  kids: [
    { id: "k1", name: "Emma", age: 12, initials: "E", color: "#D9A79A" },
    { id: "k2", name: "Liam", age: 8, initials: "L", color: "#C89268" },
  ],
};

export const greetingName = "Sarah";

// Fixed mock date to keep SSR/client identical.
export const todayLabel = "Saturday, July 4";
export const todayShort = "Jul 4";

// Rotating pool — a Vertex AI agent will select/generate one server-side each morning.
export const dailyQuotes: { text: string; author: string }[] = [
  { text: "The way to get started is to quit talking and begin doing.", author: "Walt Disney" },
  { text: "Little by little, a little becomes a lot.", author: "Tanzanian proverb" },
  { text: "You are the sky. Everything else is just the weather.", author: "Pema Chödrön" },
  {
    text: "What you do every day matters more than what you do once in a while.",
    author: "Gretchen Rubin",
  },
  {
    text: "Almost everything will work again if you unplug it for a few minutes — including you.",
    author: "Anne Lamott",
  },
  { text: "Do small things with great love.", author: "Mother Teresa" },
  {
    text: "The best time to plant a tree was 20 years ago. The second best time is now.",
    author: "Chinese proverb",
  },
];

// Deterministic pick so SSR + client match; rotates by day-of-year.
export const quoteOfTheDay =
  dailyQuotes[
    Math.floor(
      (new Date("2026-07-04T00:00:00Z").getTime() - new Date("2026-01-01T00:00:00Z").getTime()) /
        86_400_000,
    ) % dailyQuotes.length
  ];

// Vertex AI-powered "at a glance" data — this mock will be replaced by live
// Vertex AI Search + weather/time tool calls on the built app.
export const glance = {
  timeLabel: "7:42 AM",
  weather: { tempC: 18, condition: "Partly sunny", high: 24, low: 12, city: "Powell River, BC" },
  search: {
    topic: "Field trip weather · Emma's class",
    summary:
      "Cloudy morning clearing by 11 AM. Highs 22°C at the shoreline. Light rain jacket recommended for the ferry ride.",
  },
  agentSuggestions: [
    "Draft a thank-you note to Emma's teacher",
    "Move Friday dinner to 7 PM and text Mike",
    "Summarize last week's spending vs. budget",
  ],
};

// Health / lapse nudges — the Vertex AI agent notices what hasn't happened
// in a while and proposes a specific booking. User approves → agent writes
// the event to Google Calendar and confirms via SMS.
export type AgentNudge = {
  id: string;
  category: "Health" | "Movement" | "Connection" | "Home" | "Money" | "local";
  headline: string;
  reason: string; // what the agent noticed
  proposal: {
    action: "book" | "remind" | "call";
    label: string; // human-readable proposed action
    when: string; // proposed slot
    duration?: string;
  };
  detail?: string;
  date?: string;
};

export const agentNudges: AgentNudge[] = [
  {
    id: "n1",
    category: "Health",
    headline: "Annual physical is 3 months overdue",
    reason: "Last visit with Dr. Patel was Apr 12, 2025. Your notes said 'book in April.'",
    proposal: {
      action: "book",
      label: "Book physical with Dr. Patel",
      when: "Thu Jul 9 · 10:30 AM",
      duration: "45 min",
    },
  },
  {
    id: "n2",
    category: "Movement",
    headline: "No strength workout in 11 days",
    reason: "Apple Health shows cardio only. Your goal is 2× strength per week.",
    proposal: {
      action: "book",
      label: "Add 'Strength · lower body' block",
      when: "Tomorrow · 6:30 AM",
      duration: "40 min",
    },
  },
  {
    id: "n3",
    category: "Connection",
    headline: "You haven't called Mom in 18 days",
    reason: "Last call was Jun 17. You usually call every 7–10 days.",
    proposal: {
      action: "remind",
      label: "Remind me to call Mom",
      when: "Tonight · 7:00 PM",
    },
  },
  {
    id: "n4",
    category: "Health",
    headline: "Water intake down 32% this week",
    reason: "Averaging 1.1 L/day vs. your 1.8 L goal.",
    proposal: {
      action: "remind",
      label: "Add 3 hydration pings during the day",
      when: "10 AM · 1 PM · 4 PM",
    },
  },
  {
    id: "n5",
    category: "local",
    headline: "Farmers' market — Willingdon Beach",
    reason: "Saturday morning, 12 min drive. You said you love the peach vendor.",
    detail: "60+ vendors, live music at 10 AM. Kids' art table at the shore.",
    date: "Sat Jul 11 · 9:00 AM – 1:00 PM",
    proposal: {
      action: "book",
      label: "Add to Google Calendar",
      when: "Sat Jul 11 · 9:00 AM",
      duration: "2 hrs",
    },
  },
  {
    id: "n6",
    category: "local",
    headline: "Outdoor family movie — Lang Bay",
    reason: "Free screening, forecast is clear. Liam's age group.",
    detail: "'The Wild Robot' at dusk. Bring chairs. Popcorn by donation.",
    date: "Fri Jul 10 · 8:45 PM",
    proposal: {
      action: "book",
      label: "Add to Google Calendar",
      when: "Fri Jul 10 · 8:45 PM",
      duration: "1h 45m",
    },
  },
  {
    id: "n7",
    category: "local",
    headline: "Pottery drop-in for beginners",
    reason: "You bookmarked a similar class in June. Two seats left.",
    detail: "Powell River Cultural Centre. $35 · all materials included.",
    date: "Thu Jul 16 · 6:30 PM",
    proposal: {
      action: "book",
      label: "Add to Google Calendar",
      when: "Thu Jul 16 · 6:30 PM",
      duration: "2 hrs",
    },
  },
];

export type Habit = {
  id: string;
  name: string;
  done: boolean;
  streak: number;
  week: boolean[];
};

export const habits: Habit[] = [
  {
    id: "h1",
    name: "Drink water (8 glasses)",
    done: true,
    streak: 12,
    week: [true, true, true, true, true, true, false],
  },
  {
    id: "h2",
    name: "Walk 6,000 steps",
    done: false,
    streak: 4,
    week: [true, true, false, true, true, false, false],
  },
  {
    id: "h3",
    name: "Read 20 minutes",
    done: true,
    streak: 21,
    week: [true, true, true, true, true, true, true],
  },
  {
    id: "h4",
    name: "No screens after 9pm",
    done: false,
    streak: 2,
    week: [false, true, true, false, true, false, false],
  },
  {
    id: "h5",
    name: "Stretch 5 min",
    done: false,
    streak: 0,
    week: [false, false, false, false, false, false, false],
  },
];

export type Bill = {
  id: string;
  name: string;
  amountCents: number;
  dueDay: number;
  paid: boolean;
  daysUntil: number;
  subscription?: boolean;
};

export const bills: Bill[] = [
  { id: "b1", name: "Hydro Ottawa", amountCents: 14250, dueDay: 8, paid: false, daysUntil: 3 },
  { id: "b2", name: "Mortgage", amountCents: 246800, dueDay: 1, paid: true, daysUntil: -4 },
  {
    id: "b3",
    name: "Netflix",
    amountCents: 1699,
    dueDay: 12,
    paid: false,
    daysUntil: 5,
    subscription: true,
  },
  {
    id: "b4",
    name: "Rogers Internet",
    amountCents: 8999,
    dueDay: 15,
    paid: false,
    daysUntil: 8,
    subscription: true,
  },
  { id: "b5", name: "Enbridge Gas", amountCents: 6420, dueDay: 20, paid: false, daysUntil: 13 },
  {
    id: "b6",
    name: "Car insurance",
    amountCents: 18400,
    dueDay: 3,
    paid: true,
    daysUntil: -2,
    subscription: true,
  },
  {
    id: "b7",
    name: "Spotify Family",
    amountCents: 1699,
    dueDay: 18,
    paid: false,
    daysUntil: 11,
    subscription: true,
  },
  {
    id: "b8",
    name: "iCloud+ 200GB",
    amountCents: 399,
    dueDay: 22,
    paid: true,
    daysUntil: -8,
    subscription: true,
  },
];

export const formatCAD = (cents: number) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

export type ImportantDate = {
  id: string;
  title: string;
  date: string;
  dateLabel: string;
  monthLabel: string;
  daysUntil: number;
  type: "birthday" | "anniversary" | "reminder";
  who?: string;
  giftIdea?: string;
};

export const importantDates: ImportantDate[] = [
  {
    id: "d1",
    title: "Emma's birthday",
    date: "2026-07-14",
    dateLabel: "July 14, 2026",
    monthLabel: "July",
    daysUntil: 9,
    type: "birthday",
    who: "Emma turns 13",
    giftIdea: "Instant camera + film pack",
  },
  {
    id: "d2",
    title: "Anniversary",
    date: "2026-08-02",
    dateLabel: "August 2, 2026",
    monthLabel: "August",
    daysUntil: 28,
    type: "anniversary",
    who: "Sarah & Mike — 14 years",
    giftIdea: "Dinner at Beckta, sitter booked",
  },
  {
    id: "d3",
    title: "Renew passports",
    date: "2026-08-20",
    dateLabel: "August 20, 2026",
    monthLabel: "August",
    daysUntil: 46,
    type: "reminder",
    who: "Whole family",
  },
  {
    id: "d4",
    title: "Grandma's birthday",
    date: "2026-09-05",
    dateLabel: "September 5, 2026",
    monthLabel: "September",
    daysUntil: 62,
    type: "birthday",
    who: "Grandma turns 78",
    giftIdea: "Framed photo — cottage summer",
  },
  {
    id: "d5",
    title: "Liam's birthday",
    date: "2026-11-03",
    dateLabel: "November 3, 2026",
    monthLabel: "November",
    daysUntil: 121,
    type: "birthday",
    who: "Liam turns 9",
    giftIdea: "New Lego set — Star Wars",
  },
  {
    id: "d6",
    title: "Book dentist cleanings",
    date: "2026-09-15",
    dateLabel: "September 15, 2026",
    monthLabel: "September",
    daysUntil: 72,
    type: "reminder",
  },
];

export type FamilyEvent = {
  id: string;
  title: string;
  memberId: string;
  day: number;
  time: string;
  kind: "school" | "sports" | "carpool" | "meeting" | "family";
  // Enriched fields (must match @/data/types FamilyEvent): local calendar
  // date for the month grid, raw ISO start for the edit form, and the
  // per-event invite list (empty = the whole household).
  date: string;
  startsAt: string;
  attendeeIds: string[];
};

// Demo events carry the same enriched shape the live adapter produces: a
// real local `date`/`startsAt` (so the month grid places them) and an
// `attendeeIds` list (empty = the whole household). Dates are anchored to
// the current week at import time so demo always shows "this week".
type EventSeed = {
  id: string;
  title: string;
  memberId: string;
  day: number;
  time: string;
  kind: FamilyEvent["kind"];
};

function mkEvent(seed: EventSeed): FamilyEvent {
  const now = new Date();
  const curMon0 = (now.getDay() + 6) % 7;
  const d = new Date(now);
  d.setDate(now.getDate() + (seed.day - curMon0));
  const [h, m] = seed.time.split(":").map(Number);
  d.setHours(h ?? 9, m ?? 0, 0, 0);
  const date = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
  return {
    ...seed,
    time: `${String(h ?? 9).padStart(2, "0")}:${String(m ?? 0).padStart(2, "0")}`,
    date,
    startsAt: d.toISOString(),
    attendeeIds: [seed.memberId],
  };
}

const eventSeeds: EventSeed[] = [
  { id: "e1", title: "Field trip — ROM", memberId: "k1", day: 0, time: "9:00", kind: "school" },
  { id: "e2", title: "Soccer practice", memberId: "k2", day: 1, time: "17:00", kind: "sports" },
  { id: "e3", title: "Parent-teacher meeting", memberId: "k1", day: 2, time: "18:30", kind: "meeting" },
  { id: "e4", title: "Carpool — swim", memberId: "p1", day: 3, time: "16:00", kind: "carpool" },
  { id: "e5", title: "School assembly", memberId: "k2", day: 4, time: "10:00", kind: "school" },
  { id: "e6", title: "Family movie night", memberId: "p2", day: 5, time: "19:00", kind: "family" },
  { id: "e7", title: "Emma piano recital", memberId: "k1", day: 6, time: "14:00", kind: "family" },
];

export const familyEvents: FamilyEvent[] = eventSeeds.map(mkEvent);

export const todayEvents = familyEvents.slice(0, 3);

export type Homework = {
  id: string;
  kidId: string;
  subject: string;
  title: string;
  dueDate: string;
  done: boolean;
};

export const homework: Homework[] = [
  {
    id: "hw1",
    kidId: "k1",
    subject: "Math",
    title: "Fractions worksheet",
    dueDate: "Tue",
    done: false,
  },
  {
    id: "hw2",
    kidId: "k1",
    subject: "English",
    title: "Book report — Wonder",
    dueDate: "Fri",
    done: false,
  },
  {
    id: "hw3",
    kidId: "k1",
    subject: "Science",
    title: "Ecosystem poster",
    dueDate: "Mon",
    done: true,
  },
  {
    id: "hw4",
    kidId: "k2",
    subject: "Reading",
    title: "Read chapters 3–4",
    dueDate: "Wed",
    done: false,
  },
  {
    id: "hw5",
    kidId: "k2",
    subject: "Math",
    title: "Times tables (7s)",
    dueDate: "Thu",
    done: true,
  },
];

export type RewardChart = {
  id: string;
  kidId: string;
  name: string;
  points: number;
  target: number;
  reward: string;
};

export const rewardCharts: RewardChart[] = [
  {
    id: "r1",
    kidId: "k1",
    name: "Chores & kindness",
    points: 34,
    target: 50,
    reward: "Movie night pick",
  },
  {
    id: "r2",
    kidId: "k2",
    name: "Reading minutes",
    points: 180,
    target: 300,
    reward: "New Lego set",
  },
];

export type BehaviourEntry = {
  id: string;
  kidId: string;
  date: string;
  tone: "positive" | "concerning" | "neutral" | "formal";
  note: string;
};

export const behaviourLog: BehaviourEntry[] = [
  {
    id: "bl1",
    kidId: "k1",
    date: "Jul 3",
    tone: "positive",
    note: "Helped Liam with homework unprompted.",
  },
  {
    id: "bl2",
    kidId: "k2",
    date: "Jul 2",
    tone: "concerning",
    note: "Trouble settling at bedtime — 3rd night this week.",
  },
  {
    id: "bl3",
    kidId: "k1",
    date: "Jun 29",
    tone: "formal",
    note: "Teacher noted excellent presentation skills.",
  },
];

export type Goal = {
  id: string;
  name: string;
  progress: number;
  target: number;
  unit: string;
  targetDate: string;
};

export const goals: Goal[] = [
  {
    id: "g1",
    name: "Emergency fund",
    progress: 7200,
    target: 12000,
    unit: "$",
    targetDate: "Dec 2026",
  },
  {
    id: "g2",
    name: "Family Japan trip",
    progress: 9000,
    target: 9000,
    unit: "$",
    targetDate: "Jul 2027",
  },
  {
    id: "g3",
    name: "Kitchen reno",
    progress: 4500,
    target: 18000,
    unit: "$",
    targetDate: "Mar 2027",
  },
];

export type JournalEntry = {
  id: string;
  date: string;
  /** 'HH:MM' org-local wall clock, when known (SMS/web entries post-BUG-102). */
  time?: string;
  mood: number;
  gratitude?: string;
  note: string;
};

export const journal: JournalEntry[] = [
  {
    id: "j1",
    date: "Jul 4",
    mood: 8,
    gratitude: "Quiet morning coffee before the kids woke up.",
    note: "Long walk with Mike after dinner. Feeling steady.",
  },
  { id: "j2", date: "Jul 3", mood: 6, note: "Busy day — three drop-offs. Ready for the weekend." },
  {
    id: "j3",
    date: "Jul 2",
    mood: 9,
    gratitude: "Emma made everyone laugh at breakfast.",
    note: "Great day at work. Closed the Peterson file.",
  },
  {
    id: "j4",
    date: "Jun 30",
    mood: 7,
    gratitude: "The way Liam still holds my hand crossing streets.",
    note: "Slow, sunny Sunday.",
  },
  {
    id: "j5",
    date: "Jun 28",
    mood: 8,
    gratitude: "Mike unloaded the dishwasher without being asked.",
    note: "First real summer heat.",
  },
];

export const gratitudeAnniversary = { entriesCount: 47, sinceLabel: "July 2025" };

export const moodLabel = (m: number) => {
  if (m >= 9) return { emoji: "🌟", label: "Excellent" };
  if (m >= 7) return { emoji: "🙂", label: "Good" };
  if (m >= 5) return { emoji: "😐", label: "Okay" };
  if (m >= 3) return { emoji: "🌧", label: "Low" };
  return { emoji: "💤", label: "Rough" };
};

export type ShoppingCategory =
  "Produce" | "Pantry" | "Dairy & bread" | "Household" | "Kids" | "Pharmacy";

export type ShoppingItem = {
  id: string;
  name: string;
  done: boolean;
  category: ShoppingCategory;
  note?: string;
};

export const initialShopping: ShoppingItem[] = [
  { id: "s1", name: "Milk (2%)", done: false, category: "Dairy & bread", note: "4L bag" },
  { id: "s2", name: "Sourdough bread", done: false, category: "Dairy & bread" },
  { id: "s3", name: "Bananas", done: true, category: "Produce" },
  { id: "s4", name: "Chicken thighs", done: false, category: "Pantry", note: "family pack" },
  { id: "s5", name: "Emma's shampoo", done: false, category: "Pharmacy", note: "the pink one" },
  { id: "s6", name: "Blueberries", done: false, category: "Produce" },
  { id: "s7", name: "Olive oil", done: false, category: "Pantry" },
  { id: "s8", name: "Dish soap", done: true, category: "Household" },
  { id: "s9", name: "Liam's cleats — size 3", done: false, category: "Kids", note: "sports store" },
  { id: "s10", name: "Coffee beans", done: false, category: "Pantry", note: "medium roast" },
];

export const shoppingSuggestions = [
  "Yogurt (Emma's)",
  "Toothpaste",
  "Peppers",
  "Rice",
  "Paper towels",
];

export const shoppingRecent = [
  { name: "Cheddar cheese", when: "Last week" },
  { name: "School snacks", when: "Last week" },
  { name: "Toilet paper", when: "2 weeks ago" },
];

export type Vehicle = {
  id: string;
  name: string;
  plate: string;
  color: string;
  odometer: number;
  fuelMpg: number;
  fuelUnit: "L/100km" | "MPG";
  nextService: { name: string; dueIn: string; urgency: "ok" | "soon" | "overdue" };
  upcoming: { name: string; when: string }[];
  notes?: string;
  history: { date: string; service: string; km: number; costCents?: number }[];
};

export const vehicles: Vehicle[] = [
  {
    id: "v1",
    name: "2019 Honda Odyssey",
    plate: "CFYK 402",
    color: "Modern Steel",
    odometer: 84210,
    fuelMpg: 10.4,
    fuelUnit: "L/100km",
    nextService: { name: "Oil change", dueIn: "in ~800 km", urgency: "soon" },
    upcoming: [
      { name: "Winter tire swap", when: "Nov 2026" },
      { name: "Cabin air filter", when: "at 88,000 km" },
    ],
    notes: "Slight brake squeak on cold mornings — mention next service.",
    history: [
      { date: "May 2026", service: "Oil change", km: 79800, costCents: 8900 },
      { date: "Feb 2026", service: "Winter tires off", km: 76200, costCents: 6000 },
      { date: "Nov 2025", service: "Brake pads (front)", km: 72400, costCents: 42000 },
      { date: "Sep 2025", service: "Provincial inspection", km: 70100, costCents: 12000 },
    ],
  },
  {
    id: "v2",
    name: "2022 Toyota RAV4",
    plate: "BXPL 118",
    color: "Blueprint",
    odometer: 41560,
    fuelMpg: 7.8,
    fuelUnit: "L/100km",
    nextService: { name: "Tire rotation", dueIn: "in ~1,200 km", urgency: "ok" },
    upcoming: [
      { name: "First brake check", when: "at 50,000 km" },
      { name: "Warranty inspection", when: "Dec 2026" },
    ],
    history: [
      { date: "Apr 2026", service: "Oil change", km: 38900, costCents: 8500 },
      { date: "Dec 2025", service: "Winter tires on", km: 34100, costCents: 6000 },
    ],
  },
];

export type Reading = {
  id: string;
  title: string;
  author: string;
  status: "reading" | "finished" | "want";
  progress?: number;
  rating?: number;
  note?: string;
  finishedOn?: string;
};

export const reading: Reading[] = [
  {
    id: "rd1",
    title: "The Anxious Generation",
    author: "Jonathan Haidt",
    status: "reading",
    progress: 62,
    note: "Chapter on play-based childhood is landing hard.",
  },
  {
    id: "rd2",
    title: "Tomorrow, and Tomorrow, and Tomorrow",
    author: "Gabrielle Zevin",
    status: "finished",
    rating: 5,
    finishedOn: "Jun 2026",
    note: "Best novel this year. The Sam/Sadie friendship broke me open.",
  },
  {
    id: "rd3",
    title: "Slow Productivity",
    author: "Cal Newport",
    status: "reading",
    progress: 34,
  },
  {
    id: "rd4",
    title: "Demon Copperhead",
    author: "Barbara Kingsolver",
    status: "finished",
    rating: 4,
    finishedOn: "May 2026",
  },
  {
    id: "rd5",
    title: "James",
    author: "Percival Everett",
    status: "want",
  },
  {
    id: "rd6",
    title: "The Wager",
    author: "David Grann",
    status: "want",
  },
];

export type VaultItem = {
  id: string;
  title: string;
  kind: "password" | "document" | "note";
  category: "Home" | "Family" | "Money" | "Travel" | "Health";
  updated: string;
  sharedWith?: string;
};

export const vault: VaultItem[] = [
  {
    id: "vv1",
    title: "Home Wi-Fi password",
    kind: "password",
    category: "Home",
    updated: "3 months ago",
    sharedWith: "Mike",
  },
  {
    id: "vv2",
    title: "Emma's health card #",
    kind: "document",
    category: "Health",
    updated: "Last year",
  },
  {
    id: "vv3",
    title: "Mortgage renewal — TD",
    kind: "document",
    category: "Money",
    updated: "2 weeks ago",
    sharedWith: "Mike",
  },
  {
    id: "vv4",
    title: "Passport numbers (family)",
    kind: "document",
    category: "Travel",
    updated: "6 months ago",
  },
  {
    id: "vv5",
    title: "Alarm code — cottage",
    kind: "note",
    category: "Home",
    updated: "Last year",
  },
  {
    id: "vv6",
    title: "Liam's health card #",
    kind: "document",
    category: "Health",
    updated: "Last year",
  },
  { id: "vv7", title: "TFSA — RBC", kind: "document", category: "Money", updated: "1 month ago" },
  {
    id: "vv8",
    title: "Nexus card #",
    kind: "document",
    category: "Travel",
    updated: "6 months ago",
  },
];

export type HealthMetric = { label: string; value: string; unit: string; history: number[] };

export const healthMetrics: HealthMetric[] = [
  { label: "Weight", value: "148", unit: "lb", history: [151, 150, 150, 149, 149, 148, 148] },
  {
    label: "Blood pressure",
    value: "118/76",
    unit: "mmHg",
    history: [122, 120, 121, 119, 118, 118, 118],
  },
  {
    label: "Steps (today)",
    value: "5,240",
    unit: "steps",
    history: [6100, 4800, 7200, 5900, 8100, 4200, 5240],
  },
  {
    label: "Sleep (last night)",
    value: "7h 12m",
    unit: "hours",
    history: [6.5, 7.1, 6.8, 7.4, 7.0, 7.9, 7.2],
  },
];

export const smsThread = [
  { from: "me", text: "add hydro bill $142.50 due the 8th" },
  { from: "dryvn", text: "Got it — Hydro Ottawa $142.50, due Wed Jul 8. ✓" },
  { from: "me", text: "did I drink water today" },
  { from: "dryvn", text: "Yes — checked off at 8:14am. 12-day streak 🔥" },
];

export const DRYVN_NUMBER = "+16135550137";

// v1.1 additions ----------------------------------------------------------

export type Reminder = {
  id: string;
  title: string;
  time: string;
  kind: "reminder" | "task";
  done: boolean;
};

export const reminders: Reminder[] = [
  { id: "rm1", title: "Dentist — Emma", time: "9:00 AM", kind: "reminder", done: false },
  { id: "rm2", title: "Pick up dry cleaning", time: "Today", kind: "task", done: false },
  { id: "rm3", title: "Confirm summer camp deposit", time: "Today", kind: "task", done: true },
  { id: "rm4", title: "Take out recycling", time: "Tonight", kind: "task", done: false },
];

export type Elder = {
  id: string;
  name: string;
  relation: string;
  initials: string;
  color: string;
  lastCheckin: string;
  streak: number;
  missed: boolean;
  missedFor?: string;
};

export const elders: Elder[] = [
  {
    id: "el1",
    name: "Mom",
    relation: "Sarah's mother",
    initials: "M",
    color: "#B87A6E",
    lastCheckin: "3 hrs ago",
    streak: 0,
    missed: true,
    missedFor: "No reply from Mom's morning check-in — 3 hrs",
  },
  {
    id: "el2",
    name: "Dad",
    relation: "Mike's father",
    initials: "D",
    color: "#8F5F3E",
    lastCheckin: "This morning, 7:42 AM",
    streak: 28,
    missed: false,
  },
];

export type CircleBroadcast = { id: string; text: string; sentAt: string; replies: number };

export const lastBroadcast: CircleBroadcast = {
  id: "cb1",
  text: "Reminder: Emma's recital at 2pm Saturday. Bring flowers?",
  sentAt: "Yesterday, 6:14 PM",
  replies: 3,
};

export type SpendingRow = { category: string; amountCents: number; pct: number };

export const spending = {
  monthLabel: "July so far",
  totalCents: 128400,
  rows: [
    { category: "Groceries", amountCents: 42800, pct: 33 },
    { category: "Kids activities", amountCents: 31200, pct: 24 },
    { category: "Dining", amountCents: 18600, pct: 15 },
    { category: "Gas", amountCents: 14200, pct: 11 },
    { category: "Household", amountCents: 12100, pct: 9 },
    { category: "Other", amountCents: 9500, pct: 8 },
  ] as SpendingRow[],
  recentLog: [
    { id: "sl1", label: "Groceries — Loblaws", amountCents: 4700, when: "Yesterday" },
    { id: "sl2", label: "Emma soccer registration", amountCents: 18000, when: "2 days ago" },
    { id: "sl3", label: "Coffee — Bridgehead", amountCents: 620, when: "2 days ago" },
  ],
};

export type LegacyPrompt = { id: string; question: string; smsExample: string };

export const legacyPrompts: LegacyPrompt[] = [
  {
    id: "lp1",
    question: "What do you want to be remembered for?",
    smsExample: "legacy: for showing up, always",
  },
  {
    id: "lp2",
    question: "A lesson I hope Emma carries.",
    smsExample: "legacy for Emma: kindness scales",
  },
  {
    id: "lp3",
    question: "What Grandma taught me about patience.",
    smsExample: "legacy note: Grandma's patience",
  },
  {
    id: "lp4",
    question: "Something I want Liam to know at 30.",
    smsExample: "letter to Liam at 30",
  },
];

export type LegacyLetter = { id: string; title: string; date: string };

export const legacyLetters: LegacyLetter[] = [
  { id: "ll1", title: "For Emma, on her wedding day", date: "Jun 12, 2026" },
  { id: "ll2", title: "What I learned from Dad", date: "May 3, 2026" },
  { id: "ll3", title: "Liam at 18 — read together", date: "Apr 18, 2026" },
];

// -------------------- Settings → Integrations --------------------
import {
  Mail,
  CalendarDays,
  CalendarCheck,
  Wallet,
  Cloud,
  ShoppingBag,
  ListChecks,
  CalendarClock,
  ListTodo,
  HardDrive,
  FileSpreadsheet,
  Sheet,
} from "lucide-react";
import type { Integration, ApiKeyRecord, WebhookRecord, ApiUsageStat } from "./types";

// Curated to the household-core set (founder directive 2026-07-15: "do not
// attach useless integrations to a homeowner — use only the most-used").
// Niche/media/social tiles removed entirely. Real connectors carry a live
// backend; the rest are honest "coming soon" (provider-gated or still
// building) and never show a Connect button they can't honor.
export const integrations: Integration[] = [
  {
    id: "gcal",
    name: "Google Calendar",
    category: "Productivity",
    desc: "Two-way sync for family + personal.",
    Icon: CalendarDays,
    status: "connected",
    meta: "2 calendars",
  },
  {
    id: "google_tasks",
    name: "Google Tasks",
    category: "Productivity",
    desc: "Imports your shopping list as tasks; notes ride through Google Tasks. Your other task lists are counted, not yet imported.",
    Icon: ListChecks,
    status: "available",
  },
  {
    id: "google_drive",
    name: "Google Drive",
    category: "Productivity",
    desc: "Your Drive files appear in the Vault — names and links, read-only.",
    Icon: HardDrive,
    status: "available",
  },
  {
    id: "microsoft_outlook_calendar",
    name: "Outlook Calendar",
    category: "Productivity",
    desc: "Imports your Outlook events into Dryvn. Read-only for now — Dryvn won't change your Outlook calendar.",
    Icon: CalendarClock,
    status: "available",
  },
  {
    id: "microsoft_todo",
    name: "Microsoft To Do",
    category: "Productivity",
    desc: "Imports your shopping list as tasks; notes ride through Microsoft To Do. Your other task lists are counted, not yet imported.",
    Icon: ListTodo,
    status: "available",
  },
  {
    id: "icloud_calendar",
    name: "iCloud Calendar",
    category: "Productivity",
    desc: "Import your iCloud calendar — read-only, uses an app-specific password.",
    Icon: CalendarCheck,
    status: "available",
  },
  {
    id: "plaid",
    name: "Bank (Plaid)",
    category: "Money",
    desc: "See your income and manage bills — connect your bank securely via Plaid.",
    Icon: Wallet,
    status: "available",
  },
  {
    id: "gmail",
    name: "Gmail",
    category: "Communication",
    desc: "Receipts, statements, and bills — arriving once Google's review clears.",
    Icon: Mail,
    status: "coming-soon",
  },
  {
    id: "instacart",
    name: "Instacart",
    category: "Home",
    desc: "Auto-fill your shopping list — coming soon.",
    Icon: ShoppingBag,
    status: "coming-soon",
  },
  {
    id: "weather",
    name: "OpenWeather",
    category: "Home",
    desc: "Local forecast on Today — coming soon (needs your location).",
    Icon: Cloud,
    status: "coming-soon",
  },
  {
    id: "google_sheets",
    name: "Google Sheets",
    category: "Productivity",
    desc: "Writes a “Dryvn Finance” sheet — your bills, accounts, and income — to your own Google Sheets.",
    Icon: FileSpreadsheet,
    status: "available",
  },
  {
    id: "microsoft_excel",
    name: "Microsoft Excel",
    category: "Productivity",
    desc: "Writes a “Dryvn Finance” workbook — your bills, accounts, and income — to your own OneDrive.",
    Icon: Sheet,
    status: "available",
  },
];

export const integrationCategories = [
  "All",
  "Productivity",
  "Money",
  "Home",
  "Communication",
] as const;

// -------------------- Settings → API & webhooks --------------------
export const initialApiKeys: ApiKeyRecord[] = [
  {
    id: "k1",
    name: "Personal automation",
    prefix: "dryv_live_pk_7Q9…a2f",
    scopes: ["read:today", "read:health", "write:journal"],
    created: "Feb 12, 2026",
    lastUsed: "12 min ago",
  },
  {
    id: "k2",
    name: "Home Assistant",
    prefix: "dryv_live_pk_3B4…c81",
    scopes: ["read:family", "write:shopping"],
    created: "Nov 3, 2025",
    lastUsed: "yesterday",
  },
];

export const webhooks: WebhookRecord[] = [
  {
    id: "w1",
    url: "https://home.dryvn.life/hooks/journal",
    events: ["journal.created", "journal.updated"],
    status: "healthy",
    lastDelivery: "3 h ago · 200",
  },
  {
    id: "w2",
    url: "https://n8n.internal/hooks/spend",
    events: ["transaction.created"],
    status: "failing",
    lastDelivery: "2 d ago · 502",
  },
];

export const apiUsage: ApiUsageStat[] = [
  { label: "Requests · 30d", value: "24,180", delta: "+8% vs prev" },
  { label: "Errors · 30d", value: "42", delta: "0.17% rate" },
  { label: "Avg latency", value: "148 ms", delta: "p95 312 ms" },
  { label: "Rate limit", value: "600 / min", delta: "burst 60/s" },
];
